import type { PagesConfig } from '@shared/types/PagesConfig';
import { useConfigStore } from '@/Stores/config';
import { useContextStore } from '@/Stores/context';
import {
  getActiveConsentManagement,
  isConsentManagementConfigured,
  type ActiveConsentManagement,
} from '@/Integrations/Consent/consentPolicy';
import type { ConsentProvider } from '@/Integrations/Consent/types';
import { createStaticConsentProvider } from '@/Integrations/Consent/providers/staticProvider';
import { createConsentmanagerProvider } from '@/Integrations/Consent/providers/consentmanager';
import { createCookiebotProvider } from '@/Integrations/Consent/providers/cookiebot';

interface ResolvedProvider {
  provider: ConsentProvider;
  activeConsentManagement: ActiveConsentManagement | null;
}

let resolved: ResolvedProvider | null = null;

function getConfig(): PagesConfig | null {
  const configStore = useConfigStore();
  return configStore && typeof configStore !== 'string' ? configStore.config : null;
}

function resolveProvider(): ResolvedProvider {
  const contextStore = useContextStore();
  if (contextStore.isKite) {
    return {
      provider: createStaticConsentProvider({ analytics: true, marketing: true }),
      activeConsentManagement: null,
    };
  }

  const config = getConfig();
  const activeConsentManagement = getActiveConsentManagement(config);

  if (!activeConsentManagement) {
    if (isConsentManagementConfigured(config)) {
      console.warn('[Consent] Configured consent provider is not supported, allowing all');
    }
    return {
      provider: createStaticConsentProvider({ analytics: true, marketing: true }),
      activeConsentManagement: null,
    };
  }

  const provider =
    activeConsentManagement.provider === 'consentmanager.de2'
      ? createConsentmanagerProvider({ id: activeConsentManagement.id })
      : createCookiebotProvider({ id: activeConsentManagement.id });

  return { provider, activeConsentManagement };
}

function withMemoizedInit(target: ConsentProvider): ConsentProvider {
  let initPromise: Promise<void> | null = null;
  return {
    ...target,
    init() {
      if (!initPromise) {
        initPromise = target.init();
      }
      return initPromise;
    },
  };
}

function getResolvedProvider(): ResolvedProvider {
  if (!resolved) {
    resolved = resolveProvider();
    resolved.provider = withMemoizedInit(resolved.provider);
  }
  return resolved;
}

export function getConsentProvider(): ConsentProvider {
  return getResolvedProvider().provider;
}

export function getActiveConsentManagementInfo(): ActiveConsentManagement | null {
  return getResolvedProvider().activeConsentManagement;
}
